import { placeFromLookup, pointFromLookup, type MapPoint } from '../utilities/setPlaces'

/**
 * The name of a place on the map, and a place by its name or address (nobgp-router#1976): the
 * OpenStreetMap lookup (Nominatim), called from the browser.
 *
 * ⚠ What goes to OpenStreetMap: the point the reader dropped, or the text the reader typed to find
 * a place. Nothing else, and never to noBGP: the router stores the rounded point and the name of the
 * place (`utilities/setPlaces.ts` `placeFromLookup`), never the text of an address (Benoit,
 * 2026-10-20: "looked up (nearest city or something) or entered (city, state) maybe even address").
 *
 * ⚠ Nominatim's usage policy: at most one call a second, from a person, with the page as referrer.
 * The app calls it once when a location is dropped and once for each search the reader asks for.
 */
const NOMINATIM = 'https://nominatim.openstreetmap.org'

export interface LookedUp {
  point: MapPoint
  city: string
  state: string
  country: string
}

async function ask(path: string, params: Record<string, string>): Promise<unknown> {
  const query = new URLSearchParams({ format: 'jsonv2', addressdetails: '1', ...params })
  const response = await fetch(`${NOMINATIM}/${path}?${query}`, { headers: { 'Accept-Language': navigator.language || 'en' } })
  if (!response.ok) throw new Error(`the place lookup answered ${response.status}`)
  return response.json()
}

/** The town at a point, or null when there is none (the sea). `zoom` 10 asks for a town, not a street. */
export async function placeAt(point: MapPoint): Promise<LookedUp | null> {
  const answer = await ask('reverse', { lat: String(point.lat), lon: String(point.lng), zoom: '10' })
  if (!answer || (answer as { error?: unknown }).error) return null
  return { point, ...placeFromLookup(answer) }
}

/** The first place that a name or an address finds, or null. */
export async function findPlace(text: string): Promise<LookedUp | null> {
  const answers = await ask('search', { q: text, limit: '1' })
  const first = Array.isArray(answers) ? answers[0] : null
  const point = pointFromLookup(first)
  return point ? { point, ...placeFromLookup(first) } : null
}
